/**
 * This file contains a parser that takes an input goal string and produces a
 * heap state:
 *   1. Parsing the separation-logic formulas from the goal string (using the
        separation-logic parser generated by peggy from sep-grammar.g);
 *   2. Transforming the tree into a heap state.
 */

import { RenderConfig, ValueConfig, ConstrEntryConfig } from './config';
// @ts-ignore
import { parse as sepParse } from './sep-grammar.g';
import * as Sep from './sep-grammar.types';
import { assert } from './utility';

// -- AST Types ----------------------------------------------------------------

export type Goal = MaybeHProp[];

export type MaybeHProp = HProp | string;

export class HProp {
  constructor(
    public raw: string,
    public op: string,
    public args: HPropArg[],
    public ctx?: HPropCtx,
    public binder?: string
  ) {}
}

export class HProp_PointsTo extends HProp {
  constructor(
    public raw: string,
    public loc: Symbol,
    public repr: string,
    public reprArgs: Term[],
    public ctx?: HPropCtx,
    public binder?: string,
    public config?: ConstrEntryConfig
  ) {
    super(raw, 'PointsTo', [], ctx, binder);
  }

  public locUid(): string {
    return this.loc instanceof Symbol ? this.loc.uid : this.loc;
  }

  public locLabel(): string {
    return this.loc instanceof Symbol ? this.loc.label : this.loc;
  }

  public locIsGlobal(): boolean {
    return this.loc instanceof Symbol ? this.loc.isGlobal : true;
  }
}

export type HPropArg = HProp | Term | Term[];

export type HPropCtx = Sep.HPropCtx | 'DEFAULT';

export type Term = Value | Symbol | string;

// Compared to Sep.Value, Value is a class in order to use `instanceof`
export class Value {
  public uid: string;
  public label: string;
  constructor(
    public raw: string,
    public op: string,
    public args: Term[],
    config?: ValueConfig
  ) {
    this.uid = config ? this.genUid(config) : '';
    this.label = config ? this.genLabel(config) : '';
  }

  public genUid(config: ValueConfig): string {
    const opConfig = config[this.op];
    if (!opConfig || !opConfig.uid) {
      // Fallback: op-arg1-arg2-...
      const argUids = this.args.map((a) => termUid(a));
      return `${this.op}-${argUids.join('-')}`;
    }
    return opConfig.uid.replace(/\$(\d+)/g, (_, i) => {
      const idx = parseInt(i) - 1;
      assert(
        idx < this.args.length,
        `invalide idx in the uid config of ${this.op}: ${opConfig.uid}`
      );
      return termUid(this.args[idx]!);
    });
  }

  public genLabel(config: ValueConfig): string {
    const opConfig = config[this.op];
    if (!opConfig || !opConfig.label) {
      // Fallback: op(arg1, arg2, ...)
      const argLabels = this.args.map((a) => termLabel(a));
      return `${this.op}(${argLabels.join(', ')})`;
    }
    return opConfig.label.replace(/\$(\d+)/g, (_, i) => {
      const idx = parseInt(i) - 1;
      assert(
        idx < this.args.length,
        `invalide idx in the label config of ${this.op}: ${opConfig.label}`
      );
      return termLabel(this.args[idx]!);
    });
  }
}

export class Symbol {
  constructor(
    public isGlobal: boolean,
    public uid: string,
    public label: string
  ) {}
}

export function HPropArg_isTerm(x: HPropArg): x is Term {
  return !(x instanceof HProp) && !Array.isArray(x);
}

export function termUid(x: Term): string {
  return x instanceof Value ? x.uid : x instanceof Symbol ? x.uid : x;
}
export function termLabel(x: Term): string {
  return x instanceof Value ? x.label : x instanceof Symbol ? x.label : x;
}

export function termsUid(x: Term[]): string {
  return x.map(termUid).join('');
}

export function termsLabel(x: Term[]): string {
  return x.map(termLabel).join('');
}

export function termOrTermsUid(x: Term | Term[]): string {
  return Array.isArray(x) ? termsUid(x) : termUid(x);
}

export function termOrTermsLabel(x: Term | Term[]): string {
  return Array.isArray(x) ? termsLabel(x) : termLabel(x);
}

// -- Parsing ------------------------------------------------------------------

export class Parser {
  constructor(private readonly config: RenderConfig) {}

  public parse(input: string) {
    let sepGoal = sepParse(input) as Sep.Goal;
    // remove whitespace string between two hprops
    sepGoal = this.filter(sepGoal);
    // flatten rich-hprops
    sepGoal = this.flattenRichHProps(sepGoal);
    // convert to classes
    let goal: Goal = this.convertGoal(sepGoal);
    // flatten `Star`s, `Conj`s, `Disj`s
    goal.forEach((seg: MaybeHProp) => {
      if (seg instanceof HProp) this.flatten(seg);
    });
    // aggregate top-level adjacent hprops into a stars
    goal = this.aggregateTopLevel(goal);
    // aggregate `Pure`s, `PointsTo`s in arguments of `Stars`
    goal = goal.map((seg: MaybeHProp) =>
      seg instanceof HProp ? this.aggregateInStars(seg) : seg
    );
    // resolve symbols, handle `Exist`, `Forall` and `PointsTo`
    goal = this.resolveSymbols(goal);
    return goal;
  }

  private filter(goal: Sep.Goal): Sep.Goal {
    const res: Sep.MaybeHProp[] = [];
    goal.forEach((seg, idx) => {
      if (
        typeof seg === 'string' &&
        seg.trim().length === 0 &&
        idx - 1 >= 0 &&
        (Sep.isRichHProp(goal[idx - 1]) || Sep.isHProp(goal[idx - 1])) &&
        idx + 1 < goal.length &&
        (Sep.isRichHProp(goal[idx + 1]) || Sep.isHProp(goal[idx + 1]))
      ) {
        return;
      }
      res.push(seg);
    });
    return res;
  }

  private flattenRichHProps(goal: Sep.Goal): Sep.Goal {
    return goal.flatMap((seg) => {
      if (
        typeof seg === 'object' &&
        'kind' in seg &&
        seg.kind === 'rich-hprop'
      ) {
        const x = seg as Sep.RichHProp;
        return [
          ...(x.prefix ? [x.prefix] : []),
          x.hprop,
          ...(x.postfix ? [x.postfix] : []),
        ];
      }
      return [seg];
    });
  }

  private convertGoal(goal: Sep.Goal): Goal {
    const valueConfig: ValueConfig = this.config.value;
    return goal
      .map(convertMaybeHProp)
      .reduce((acc: MaybeHProp[], seg: MaybeHProp) => {
        if (typeof seg === 'string') {
          const last = acc[acc.length - 1];
          if (typeof last === 'string') acc[acc.length - 1] += seg;
          else acc.push(seg);
        } else acc.push(seg);
        return acc;
      }, []);

    function convertValue(x: Sep.Value): Value {
      return new Value(x.raw, x.op, x.args.map(convertMaybeValue), valueConfig);
    }

    function convertMaybeValue(x: Sep.MaybeValue): Term {
      if (Sep.isValue(x)) return convertValue(x);
      if (Array.isArray(x))
        // Value inside a MaybeValue is a Value inside a Gallina term,
        // directly convert it into a string.
        return x
          .map((y) => (Sep.isValue(y) ? convertValue(y).label : y))
          .join('');
      return x as string;
    }

    function convertHPropArg(x: Sep.HPropArg): HPropArg {
      if (Array.isArray(x)) return x.map(convertMaybeValue);
      if (typeof x === 'object' && 'kind' in x) {
        if (x.kind === 'hprop') return convertHProp(x as Sep.HProp);
        if (x.kind === 'value') return convertValue(x as Sep.Value);
      }
      return x as string;
    }

    function convertHProp(x: Sep.HProp): HProp {
      return new HProp(
        x.raw,
        x.op,
        x.args.map(convertHPropArg),
        x.ctx,
        x.binder
      );
    }

    function convertMaybeHProp(x: Sep.MaybeHProp): MaybeHProp {
      return typeof x === 'object' && 'kind' in x
        ? x.kind === 'hprop'
          ? convertHProp(x as Sep.HProp)
          : // parse values in segments into string
            convertValue(x as Sep.Value).label
        : (x as string);
    }
  }

  /**
   * Flatten nestest binary ops (e.g. `Star`s) into n-ary op (e.g. `Stars`).
   * Mutate in place.
   */
  private flatten(hprop: HProp): void {
    const toFlattenOps = ['Star', 'Conj', 'Disj'];

    function rec(x: HProp): void {
      x.args.forEach((arg: HPropArg) => {
        if (arg instanceof HProp) rec(arg);
      });
      if (toFlattenOps.includes(x.op)) {
        const op = x.op + 's';
        x.op = op;
        x.args = x.args.flatMap((a: HPropArg) =>
          a instanceof HProp && a.op === op ? a.args : [a]
        );
      }
    }

    rec(hprop);
  }

  /**
   * Aggregate top-level adjacent hprops into a `Stars`.
   * e.g., the goal [string1, hprop1, hprop2, hprop3, string2] will be
   *  tranformed to [string1, Stars(hprop1, hprop2, hprop3), string2]
   */
  private aggregateTopLevel(goal: Goal): Goal {
    return goal.reduce((acc: MaybeHProp[], seg: MaybeHProp) => {
      if (seg instanceof HProp) {
        const last = acc[acc.length - 1];
        if (last instanceof HProp) {
          if (last.op === 'Stars') {
            last.args.push(seg);
            last.raw = last.raw + seg.raw;
            if (last.ctx !== seg.ctx) last.ctx = undefined;
          } else {
            acc[acc.length - 1] = new HProp(
              last.raw + seg.raw,
              'Stars',
              [last, seg],
              last.ctx === seg.ctx ? last.ctx : undefined
            );
          }
        } else {
          // last is a string
          acc.push(seg);
        }
      } else {
        // seg is a string
        acc.push(seg);
      }
      return acc;
    }, []);
  }

  /**
   * Aggregate repeated `Pure` and `PointsTo` in `Stars`. E.g., tranform
   * `Stars(Pure(P1), ..., Pure(Pn), ...)` into `Stars(Pures(Pure(P1), ..., Pure(Pn)), ...)`.
   */
  private aggregateInStars(hprop: HProp): HProp {
    // Ordered list: generate `Stars(Pures(...), PointsTos(...), ...)`
    const toCoalesceOps = ['Pure', 'PointsTo'].reverse();

    function aggregateCtx(args: HPropArg[]): HPropCtx | undefined {
      let ctx: HPropCtx | undefined = undefined;
      args.forEach((x, idx) => {
        if (x instanceof HProp) {
          if (idx === 0) ctx = x.ctx;
          else if (ctx !== x.ctx) ctx = undefined;
        }
      });
      return ctx;
    }

    function rec(x: HProp, op: string): HProp {
      const isOp = (y: HPropArg) => y instanceof HProp && y.op === op;
      const notOp = (y: HPropArg) => !isOp(y);
      const args = x.args.map((arg) =>
        arg instanceof HProp ? rec(arg, op) : arg
      );
      if (x.op === 'Stars') {
        const isOpArgs = args.filter(isOp);
        const ops = new HProp('', op + 's', isOpArgs, aggregateCtx(isOpArgs));
        if (ops.args.length !== 0)
          return new HProp(
            x.raw,
            x.op,
            [ops, ...args.filter(notOp)],
            x.ctx,
            x.binder
          );
      }
      return new HProp(x.raw, x.op, args, x.ctx, x.binder);
    }

    let x = hprop;
    toCoalesceOps.forEach((op) => (x = rec(x, op)));
    return x;
  }

  /**
   * Resolve symbols, handle `Exist`, `Forall` and `PointsTo`:
   * - lift local `Exist` quantifiers to the global scope.
   * - parse `PointsTo` to the `HProp` subtype `HProp_PointsTo`
   */
  private resolveSymbols(goal: Goal): Goal {
    const gensym: Record<string, number> = {};
    const ctx: Record<string, Symbol> = {};
    const valueConfig: ValueConfig = this.config.value;

    function next(prefix: string): number {
      if (!(prefix in gensym)) gensym[prefix] = 0;
      return gensym[prefix]!++;
    }

    const resolve = (s: string): Symbol | string => ctx[s] ?? s;

    function registerLocal(s: string): void {
      const num = next(s);
      ctx[s] = new Symbol(false, s + '$' + num, `${s}${num}`);
    }

    function registerGlobal(uid: string, label: string): Symbol {
      if (uid in ctx) return ctx[uid]!;
      ctx[uid] = new Symbol(true, uid, label);
      return ctx[uid];
    }

    return goal.map((x) => (x instanceof HProp ? resolveHProp(x) : x));

    function resolveHProp(x: HProp): HProp {
      switch (x.op) {
        case 'Exist':
        case 'Forall': {
          assert(x.args.length === 2, `${x.op} expects 2 arguments`);
          assert(
            typeof x.args[0] === 'string',
            `[parser:resolveHProp] ${x.op} expect the 1st argument to be a string, got ${JSON.stringify(x.args[0])}`
          );
          const binder = x.args[0] as string;
          const y: HPropArg = x.args[1]!;
          registerLocal(binder);
          return resolveHProp(
            y instanceof HProp
              ? y
              : new HProp(termOrTermsLabel(y), 'Opaque', [y], x.ctx, x.binder)
          );
        }
        case 'PointsTo': {
          // Arguments after the first two will be discarded.
          assert(x.args.length >= 2, 'PointsTo expects at least 2 arguments');
          const loc_arg = resolveHPropArg(x.args[0]!);
          assert(
            !(loc_arg instanceof HProp),
            `[parser:resolveHProp] PointsTo: 1st argument should not be a HProp, got ${JSON.stringify(x.args[0]!)}`
          );
          const loc = registerGlobal(
            termOrTermsUid(loc_arg),
            termOrTermsLabel(loc_arg)
          );
          /**
           * Note: in the case of
           * `[PointsTo(p1, ...), PointsTo(p2, Value('MPair', [p1, p3])), PointsTo(p3, ...)]`,
           * `p1` is registered as a global symbol before resolving the second PointsTo,
           * so argument `p1` is resolved to a symbol while `p3` is not.
           */
          const repr_arg = resolveHPropArg(x.args[1]!);
          assert(
            repr_arg instanceof Value,
            `2nd argument of PointsTo should be a Value`
          );
          const repr = repr_arg.op;

          return new HProp_PointsTo(
            x.raw,
            loc,
            repr,
            repr_arg.args.map((a) =>
              // trim and collapse consecutive whitespaces
              typeof a === 'string' ? a.trim().replaceAll(/\s+/g, ' ') : a
            ),
            x.ctx,
            x.binder
          );
        }
        default:
          return new HProp(
            x.raw,
            x.op,
            x.args.map(resolveHPropArg),
            x.ctx,
            x.binder
          );
      }
    }

    function resolveHPropArg(x: HPropArg): HPropArg {
      if (x instanceof HProp) return resolveHProp(x);
      if (Array.isArray(x)) return x.map(resolveTerm);
      return resolveTerm(x as Term);
    }

    function resolveTerm(x: Term): Term {
      if (x instanceof Value) return resolveValue(x);
      return resolve(x as string);
    }

    function resolveValue(x: Value): Value {
      return new Value(x.raw, x.op, x.args.map(resolveTerm), valueConfig);
    }
  }
}
